import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

export const useFeedback = (message, chatId, handleFeedbackSubmit) => {
  // Map backend feedback values to UI state values
  const getFeedbackUIState = (dbFeedback) => {
    if (dbFeedback === 'helpful') return 'up';
    if (dbFeedback === 'notHelpful') return 'down';
    return null;
  };

  const [feedback, setFeedback] = useState(getFeedbackUIState(message?.userFeedback));
  
  // Negative feedback form state
  const [showNegativeFeedbackForm, setShowNegativeFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackScreenshot, setFeedbackScreenshot] = useState(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isTakingScreenshot, setIsTakingScreenshot] = useState(false);
  const feedbackFormRef = useRef(null);

  const rawMessageId = message?._id;
  const messageIsTemporary = Boolean(message?.isTemporary);

  const getStableMessageId = useCallback(() => {
    if (!rawMessageId || messageIsTemporary) {
      return null;
    }

    const idString = typeof rawMessageId === 'string'
      ? rawMessageId
      : (typeof rawMessageId.toString === 'function' ? rawMessageId.toString() : null);

    if (!idString) {
      return null;
    }

    if (idString.startsWith('temp-')) {
      return null;
    }

    return idString;
  }, [rawMessageId, messageIsTemporary]);

  const handleFeedback = async (isHelpful) => {
    // Don't allow feedback on temporary chat IDs (they start with 'a-')
    if (!chatId || chatId.toString().startsWith('a-') || !message?._id) {
      console.error('Cannot submit feedback for temporary chat or missing message ID');
      return;
    }

    const stableMessageId = getStableMessageId();
    if (!stableMessageId) {
      console.warn('Cannot submit feedback until the response has been saved', {
        chatId,
        messageId: rawMessageId
      });
      return;
    }

    // If clicking the already-selected icon, clear feedback and let user pick again
    const currentIsUp = feedback === 'up';
    const currentIsDown = feedback === 'down';
    if ((isHelpful && currentIsUp) || (!isHelpful && currentIsDown)) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${stableMessageId}/feedback`,
          { clear: true },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setFeedback(null);
      } catch (error) {
        console.error('Error clearing feedback:', error);
      }
      return;
    }

    // If positive feedback, submit immediately
    if (isHelpful) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${stableMessageId}/feedback`,
          { isHelpful: true },
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            } 
          }
        );
        
        // Update local feedback state
        setFeedback('up');
        
        // Call the parent's feedback handler to show notification
        if (handleFeedbackSubmit) {
          handleFeedbackSubmit();
        }
      } catch (error) {
        console.error('Error submitting positive feedback:', error);
      }
    } else {
      // For negative feedback, show the detailed feedback form
      console.log('👎 Negative feedback button clicked, showing form');
      setShowNegativeFeedbackForm(true);
      
      // Auto-scroll if this is the last message
      setTimeout(() => {
        if (feedbackFormRef.current) {
          feedbackFormRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 100);
    }
  };

  const handleDetailedFeedbackSubmit = async () => {
    const stableMessageId = getStableMessageId();
    if (!stableMessageId) {
      console.warn('Cannot submit detailed feedback until the response has been saved', {
        chatId,
        messageId: rawMessageId
      });
      return;
    }

    console.log('🔍 handleDetailedFeedbackSubmit called');
    console.log('📊 Feedback data:', {
      feedbackText: feedbackText,
      hasScreenshot: !!feedbackScreenshot,
      screenshotName: feedbackScreenshot?.name,
      chatId: chatId,
      messageId: stableMessageId,
      isSubmittingFeedback
    });

    if (isSubmittingFeedback) {
      console.log('⏸️ Already submitting feedback, skipping...');
      return;
    }

    try {
      setIsSubmittingFeedback(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('isHelpful', false);
      formData.append('feedbackText', feedbackText);
      
      if (feedbackScreenshot) {
        formData.append('screenshot', feedbackScreenshot);
        console.log('Added screenshot to form data:', feedbackScreenshot.name);
      }

      console.log('🚀 Sending feedback request to:', `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${stableMessageId}/feedback`);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${stableMessageId}/feedback`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      console.log('✅ Feedback submitted successfully:', response.data);
      
      // Update local feedback state
      setFeedback('down');
      
      // Hide the form and reset state
      setShowNegativeFeedbackForm(false);
      setFeedbackText('');
      setFeedbackScreenshot(null);
      
      // Call the parent's feedback handler to show notification
      if (handleFeedbackSubmit) {
        handleFeedbackSubmit();
      }
    } catch (error) {
      console.error('❌ Error submitting detailed feedback:', error);
      console.error('📋 Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
    } finally {
      setIsSubmittingFeedback(false);
      console.log('🏁 Feedback submission completed, isSubmittingFeedback set to false');
    }
  };

  const handleCloseWithoutSubmit = () => {
    setShowNegativeFeedbackForm(false);
    setFeedbackText('');
    setFeedbackScreenshot(null);
    setIsTakingScreenshot(false);
  };

  const handleFeedbackCancel = async () => {
    const stableMessageId = getStableMessageId();
    if (stableMessageId && chatId && !chatId.toString().startsWith('a-')) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/${stableMessageId}/feedback`,
          { isHelpful: false },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setFeedback('down');
        if (handleFeedbackSubmit) {
          handleFeedbackSubmit();
        }
      } catch (error) {
        console.error('Error submitting negative feedback (I\'d rather not):', error);
      }
    }
    setShowNegativeFeedbackForm(false);
    setFeedbackText('');
    setFeedbackScreenshot(null);
    setIsTakingScreenshot(false);
  };

  const handleScreenshotUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFeedbackScreenshot(file);
    }
  };

  const handleTakeScreenshot = async () => {
    setIsTakingScreenshot(true);
    
    try {
      console.log('📸 Taking screenshot...');
      
      // Check if screen capture is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error('Screen capture not supported in this browser');
      }
      
      // Request screen capture
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen'
        }
      });

      // Create video element to capture the stream
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      // Wait for video to load
      video.addEventListener('loadedmetadata', () => {
        // Create canvas to capture frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        // Stop the stream
        stream.getTracks().forEach(track => track.stop());
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a File object from the blob
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const file = new File([blob], `screenshot-${timestamp}.png`, {
              type: 'image/png',
              lastModified: Date.now()
            });
            
            console.log('📸 Screenshot captured:', file.name, file.size);
            setFeedbackScreenshot(file);
            setIsTakingScreenshot(false);
          }
        }, 'image/png');
      });

      // Handle video load error
      video.addEventListener('error', () => {
        console.error('❌ Error loading video for screenshot');
        setIsTakingScreenshot(false);
      });

    } catch (error) {
      console.error('❌ Error taking screenshot:', error);
      setIsTakingScreenshot(false);
      
      if (error.name === 'NotAllowedError') {
        console.log('🚫 User denied screen capture permission');
      } else if (error.name === 'NotSupportedError') {
        console.log('🚫 Screen capture not supported in this browser');
      }
    }
  };

  return {
    feedback,
    showNegativeFeedbackForm,
    feedbackText,
    setFeedbackText,
    feedbackScreenshot,
    isSubmittingFeedback,
    isTakingScreenshot,
    feedbackFormRef,
    handleFeedback,
    handleDetailedFeedbackSubmit,
    handleFeedbackCancel,
    handleCloseWithoutSubmit,
    handleScreenshotUpload,
    handleTakeScreenshot
  };
}; 